/******************************************************************************
 * 
 * Strauss Motion
 * 
 * Copyright (c) 2022 Robimo GmbH
 *
 * Authors: Daniel Martinek
 *
 *****************************************************************************/
import { Col, Container, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { UIState, ConfigState, ContactState, LibraryData, PricingData, CountryData } from '../store/interfaces';
 
import LanguageSwitcherContainer from '../containers/LanguageSwitcherContainer';

import styles from '../styles/customerscreen.module.scss'
import { LogoText } from './LogoText';

import {ReactComponent as IconArrowLeft} from '../icons/arrow_left.svg';
import {ReactComponent as IconArrowRight} from '../icons/arrow_right.svg';

import { getCountry } from '../i18n';
import { Progress } from './Progress';
import CurrentConfigContainer from '../containers/CurrentConfigContainer';
import { isMobile } from 'react-device-detect';
import { addPrice, applyVat, getFinalPricingNoVat, getPricingTextLong, getShippingPrice, getVat } from '../helpers/pricing';
import LinkFooterContainer from '../containers/LinkFooterContainer';

 interface CustomerscreenProps {
    ui: UIState;
    config: ConfigState;
    contact: ContactState;
    library: LibraryData;
    onUiGoTo: (route: string) => void;
    onUiSetMobileOrderStep: (step: string) => void;
    onConfigSetOrderFor: (value: string) => void;
    onConfigSetCustomerNumber: (value: string) => void;
    onConfigSetFirstName: (value: string) => void;
    onConfigSetLastName: (value: string) => void;
    onConfigSetCountry: (value: string) => void;
    onConfigSetZipCode: (value: string) => void;
    onConfigSetCity: (value: string) => void;
    onConfigSetAddress: (value: string) => void;
    onConfigSetTitle: (value: string) => void;
    onConfigSetOccupation: (value: string) => void;
    onConfigSetEmail: (value: string) => void;
    onConfigSetPhone: (value: string) => void;
    onConfigSetComment: (value: string) => void;
    onConfigSend: (lang: string) => void;
    onUISetVat: (vat: boolean) => void;
    onUiSetCountry: (country: CountryData) => void;
    onUiShowCountryChangeInfo: (showCountryChangeInfo: boolean) => void;
}

function isEmptyOrSpaces(str: string | null){
    return str === null || str.match(/^ *$/) !== null;
}

export function Customerscreen(props: CustomerscreenProps) {
    const {t, i18n} = useTranslation();

    let allowedToContinue = 
        !isEmptyOrSpaces(props.contact.title) &&
        !isEmptyOrSpaces(props.contact.firstName) &&
        !isEmptyOrSpaces(props.contact.lastName) &&
        !isEmptyOrSpaces(props.contact.email) &&
        !isEmptyOrSpaces(props.contact.phone) && props.contact.phone.length > 3 &&
        !isEmptyOrSpaces(props.contact.occupation) &&
        !isEmptyOrSpaces(props.contact.country) &&
        !isEmptyOrSpaces(props.contact.zipCode) &&
        !isEmptyOrSpaces(props.contact.city) &&
        !isEmptyOrSpaces(props.contact.address)
    ;

    let customerNumberError = false;
    //@ts-ignore
    if(props.contact.customerNumber.length > 0 && !/^[0-9]{8}$/.test(props.contact.customerNumber)) {
        allowedToContinue = false;
        customerNumberError = true;
    }

    let phoneError = false;
    //@ts-ignore
    if(props.contact.phone.length > 3 && !/^[\+0-9 ]{8,17}$/.test(props.contact.phone)) {
        allowedToContinue = false;
        phoneError = true;
    }

    let zipError = false;
    let phoneCode = '+49';
    if (props.ui.country.id === 'ch') {
        phoneCode = '+41'
        if(!/^[a-zA-Z0-9]{4}$/.test(props.contact.zipCode)) {
            allowedToContinue = false;
    
            if(props.contact.zipCode.length > 0) {
                zipError = true;
            }
        }
    } else if (props.ui.country.id === 'at') {
        phoneCode = '+43'
        if(!/^[a-zA-Z0-9]{4}$/.test(props.contact.zipCode)) {
            allowedToContinue = false;
    
            if(props.contact.zipCode.length > 0) {
                zipError = true;
            }
        }
    } else {
        if(!/^[a-zA-Z0-9]{5,6}$/.test(props.contact.zipCode)) {
            allowedToContinue = false;
    
            if(props.contact.zipCode.length > 0) {
                zipError = true;
            }
        }
    }

    let phoneNumber = props.contact.phone;
    if (props.contact.phone.startsWith('+')) {
        phoneCode = props.contact.phone.substring(0, 3);
        phoneNumber = props.contact.phone.substring(3);
    }

    let totalAmountNoVat: PricingData|null = null;
    let shippingPrice: PricingData|null = null;
    let totalVat: PricingData|null = null;
    let totalAmountVat: PricingData|null = null;

    if (isMobile) {
        totalAmountNoVat = getFinalPricingNoVat(props.library, props.config.products);
        shippingPrice = getShippingPrice(props.library, totalAmountNoVat);
        totalVat = getVat(totalAmountNoVat, props.ui.country.vat);
        totalAmountVat = addPrice(applyVat(totalAmountNoVat, props.ui.country.vat), shippingPrice);    
    }

    return (
        <Container fluid className={styles.container}>
            <div className={styles.content}>
                <div className={styles.header}>
                    <LogoText size={'small'} black={true}/>
                </div>
                <div className={styles.products}>
                    <div className={styles.productsLeft}>
                        <div className={styles.productsHeader}>
                            <button className={`${styles.buttonWhite}`} onClick={(evt) => {props.onUiGoTo('orderscreen')}}><IconArrowLeft/>{t('customerscreen.backToOrderscreen')}</button>
                            <Progress progress={0.5}/>
                        </div>
                        <div className={styles.contentForm}>
                            <Row>
                                <Col xs={12}>
                                    <Row>
                                        <Col xs={12} sm={6}>
                                            <label>
                                                {t('customerscreen.contact.orderFor')}
                                                <div className={styles.radiobuttonContainer}>
                                                    <label className={styles.radiobutton}>
                                                        {t('customerscreen.orderFor.company')}
                                                        <input type="radio" name="orderFor" value="company" checked={props.contact.orderFor === 'company'} onChange={(evt) => {props.onConfigSetOrderFor('company');}}></input>
                                                        <span className={styles.checkmark}></span>
                                                    </label>
                                                    <label className={styles.radiobutton}>
                                                        {t('customerscreen.orderFor.private')}
                                                        <input type="radio" name="orderFor" value="private" checked={props.contact.orderFor === 'private'} onChange={(evt) => {props.onConfigSetOrderFor('private');}}></input>
                                                        <span className={styles.checkmark}></span>
                                                    </label>
                                                </div>
                                            </label>
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <label>{t('customerscreen.contact.title')}
                                            <Form.Select aria-label="" onChange={(evt) => {props.onConfigSetTitle(evt.target.value)}}>
                                                    <option value=''> </option>
                                                    <option value='female'>{t('customerscreen.contact.titleFemale')}</option>
                                                    <option value='male'>{t('customerscreen.contact.titleMale')}</option>
                                                </Form.Select>
                                            </label>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xs={12} sm={6}>
                                            <label>{props.contact.orderFor === 'private' ? t('customerscreen.contact.firstName') : t('customerscreen.contact.companyName')}<input type="text" value={props.contact.firstName} onChange={(evt) => {props.onConfigSetFirstName(evt.target.value)}}></input></label>
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <label>{props.contact.orderFor === 'private' ? t('customerscreen.contact.lastName') : t('customerscreen.contact.companyContact')}<input type="text" value={props.contact.lastName} onChange={(evt) => {props.onConfigSetLastName(evt.target.value)}}></input></label>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xs={12} sm={3}>
                                            <label>{t('customerscreen.contact.country')}
                                                <Form.Select aria-label="" onChange={(evt) => {
                                                    props.onConfigSetCountry(evt.target.value);
                                                    
                                                    // Todo(daniel): Create common list of countries and only set by id.
                                                    if(evt.target.value === 'de') {
                                                        if(props.ui.country.id !== 'de') {
                                                            props.onUiShowCountryChangeInfo(true);
                                                        }
                                                        props.onUiSetCountry({
                                                            id: 'de',
                                                            currency: 'eur',
                                                            vat: 0.19
                                                        });
                                                    } else if(evt.target.value === 'at') {
                                                        if(props.ui.country.id !== 'at') {
                                                            props.onUiShowCountryChangeInfo(true);
                                                        }
                                                        props.onUiSetCountry({
                                                            id: 'at',
                                                            currency: 'eur',
                                                            vat: 0.2
                                                        });
                                                    } else if(evt.target.value === 'ch') {
                                                        //if(props.ui.country.id !== 'ch') {
                                                        //    props.onUiShowCountryChangeInfo(true);
                                                        //}
                                                        //props.onUiSetCountry({
                                                        //    id: 'ch',
                                                        //    currency: 'chf',
                                                        //    vat: 0.077
                                                        //});
                                                    }
                                                }} value={props.ui.country.id}>
                                                    <option value='de'>{getCountry('DE', i18n.language)}</option>
                                                    <option value='at'>{getCountry('AT', i18n.language)}</option>
                                                    {/*<option value='ch'>{getCountry('CH', i18n.language)}</option>*/}
                                                </Form.Select>
                                            </label>
                                        </Col>
                                        <Col xs={12} sm={3} className={zipError ? styles.error : ''}>
                                            <label>{t('customerscreen.contact.zipCode')}<input type="text" value={props.contact.zipCode} onChange={(evt) => {props.onConfigSetZipCode(evt.target.value)}}></input></label>
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <label>{t('customerscreen.contact.city')}<input type="text" value={props.contact.city} onChange={(evt) => {props.onConfigSetCity(evt.target.value)}}></input></label>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            <label>{t('customerscreen.contact.address')}<input type="text" value={props.contact.address} onChange={(evt) => {props.onConfigSetAddress(evt.target.value)}}></input></label>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col xs={12}>
                                    <Row>
                                        <Col xs={12} sm={6} className={customerNumberError ? styles.error : ''}>
                                            <label>{t('customerscreen.contact.customerNumber')}<input type="text" value={props.contact.customerNumber} onChange={(evt) => {props.onConfigSetCustomerNumber(evt.target.value)}}></input></label>
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <label>{props.contact.orderFor === 'private' ? t('customerscreen.contact.occupation') : t('customerscreen.contact.sector')}<input type="text" value={props.contact.occupation} onChange={(evt) => {props.onConfigSetOccupation(evt.target.value)}}></input></label>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xs={12} sm={6}>
                                            <label>{t('customerscreen.contact.email')}<input type="text" value={props.contact.email} onChange={(evt) => {props.onConfigSetEmail(evt.target.value)}}></input></label>
                                        </Col>
                                        <Col xs={12} sm={6} className={phoneError ? styles.error : ''}>
                                            <label className={styles.phoneLabel}>{t('customerscreen.contact.phone')}<input type="text" value={phoneNumber} onChange={(evt) => {props.onConfigSetPhone(phoneCode + evt.target.value);}}></input> 
                                                <Form.Select className={styles.phoneCode} aria-label="" onChange={(evt) => {props.onConfigSetPhone(evt.target.value + phoneNumber)}} disabled={true} value={phoneCode}>
                                                    {/*<option value='+41'>+41</option>*/}
                                                    <option value='+43'>+43</option>
                                                    <option value='+49'>+49</option>
                                                </Form.Select>
                                            </label>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            <label>{t('customerscreen.contact.comment')}<textarea onChange={(evt) => {props.onConfigSetComment(evt.target.value)}} defaultValue={props.contact.comment}></textarea></label>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </div>
                        {isMobile && totalAmountNoVat !== null && totalVat !== null && totalAmountVat !== null &&
                            <div className={styles.productSumContainer}>
                                <div className={styles.productSum}>
                                    <div className={`${styles.productColumns} ${styles.borderBottom}`}>
                                        <div className={styles.col0}>
                                            <span className={styles.sumBold}>{t('orderscreen.sum.totalNoVat')}</span>
                                        </div>
                                        <div className={styles.col1}>
                                            <span className={styles.sumBold}>{getPricingTextLong(i18n.language, props.ui.country, totalAmountNoVat)}</span>
                                        </div>
                                        <div className={styles.col0}>
                                            <span>{t('orderscreen.sum.shipping')}</span>
                                        </div>
                                        <div className={styles.col1}>
                                            <span>{t('orderscreen.sum.free')}</span>
                                        </div>
                                        <div className={styles.col0}>
                                            <span>{t('orderscreen.sum.additionalVat')}</span>
                                        </div>
                                        <div className={styles.col1}>
                                            <span>{getPricingTextLong(i18n.language, props.ui.country, totalVat)}</span>
                                        </div>
                                    </div>
                                    <div className={styles.productColumns}>
                                        <div className={styles.col0}>
                                            <span className={styles.sumTotal}>{t('orderscreen.sum.totalWithVat')}</span>
                                        </div>
                                        <div className={styles.col1}>
                                            <span className={styles.sumTotalBold}>{getPricingTextLong(i18n.language, props.ui.country, totalAmountVat)}</span>
                                        </div>
                                    </div>
                                    <span>{t('orderscreen.sum.withVat')}</span>
                                </div>
                            </div>
                        }
                        <div className={styles.footerButtons}>
                            <button className={`${styles.buttonWhite}`} onClick={(evt) => {
                                if(isMobile) {
                                    props.onUiSetMobileOrderStep('order');
                                    props.onUiGoTo('editscreen');
                                } else {
                                    props.onUiGoTo('orderscreen');
                                }
                            }}><IconArrowLeft/>{isMobile ? t('misc.back') : t('customerscreen.backToOrderscreen')}</button>
                            <button data-testid="mymotion_customerDataNextStep" className={`${styles.buttonPrimary}`} disabled={!allowedToContinue} onClick={(evt) => {
                                if(isMobile) {
                                    props.onUiSetMobileOrderStep('final');
                                    props.onUiGoTo('editscreen');
                                } else {
                                    props.onUiGoTo('finalscreen')
                                }
                            }}>{t('orderscreen.nextStep')}<IconArrowRight/></button>
                        </div>
                    </div>    
                    {!isMobile &&  
                        <div className={styles.productsRight}>
                            <CurrentConfigContainer/>
                        </div>  
                    }
                </div>            
                <div className={styles.footer}><LanguageSwitcherContainer black/><span className={styles.footerSeparator}></span><LinkFooterContainer fullWidth={true}/></div>
            </div>
        </Container>
    );
};